import { createContext, useContext, useMemo, type ReactNode } from "react";

import { useMyPayments } from "@/hooks/usePayments";
import type { PendingPayment } from "@/types/payments";

/**
 * Makes an unfinished payment *visible* without taking the app hostage.
 *
 * WHY THIS DOES NOT REDIRECT
 * --------------------------
 * It used to. That was wrong: a pending payment would bounce the user from
 * /home to the payment step, and every attempt to navigate elsewhere -- even
 * by editing the URL directly -- bounced them straight back. Owing money is
 * not a reason to confiscate the rest of the product; the user may reasonably
 * want to review their goals, re-read the advisor list, or simply leave and
 * come back later. Trapping them also makes an ordinary "I'll pay in a
 * minute" feel like a broken app.
 *
 * So the rule is: SHOW, never force. This component resolves the pending
 * payment once and publishes it via context; pages render a resume affordance
 * (see PendingPaymentBanner) that the user clicks when *they* decide to.
 *
 * This was never the security boundary and still isn't -- the server checks
 * entitlement on every write that constitutes a functioning board, so a user
 * browsing unpaid can look but not act. That separation is what makes the
 * permissive behaviour here safe.
 *
 * Fails OPEN on error: a transient payments-API blip must not degrade the app
 * for users who owe nothing.
 */

interface PendingPaymentContextValue {
  /** The user's unfinished payment for this scope, if any. */
  pending: PendingPayment | null;
  /** False until a real server answer is in hand. */
  isResolved: boolean;
}

const PendingPaymentContext = createContext<PendingPaymentContextValue>({
  pending: null,
  isResolved: false,
});

/**
 * The pending payment for the surrounding PaymentGuard, or null.
 *
 * Returns null (not a throw) outside a provider, so a page can call this
 * unconditionally without caring whether it happens to be mounted under one.
 */
export const usePendingPayment = () => useContext(PendingPaymentContext);

interface PaymentGuardProps {
  children: ReactNode;
  /** Scope to one program. Omit to track the user's PAB activation. */
  programId?: number | null;
}

export const PaymentGuard = ({ children, programId }: PaymentGuardProps) => {
  const { data, isError } = useMyPayments();

  const value = useMemo<PendingPaymentContextValue>(() => {
    if (!data || isError) return { pending: null, isResolved: false };

    const pending =
      data.pendingPayments.find((p) =>
        programId == null ? p.purpose === "PAB_ACTIVATION" : p.programId === programId,
      ) ?? null;

    if (!pending) return { pending: null, isResolved: true };

    // An entitlement granted after the payment row was created (a webhook that
    // landed between fetches) means they are already paid up -- don't nag.
    const alreadyEntitled =
      pending.programId != null &&
      data.entitlements.some(
        (e) => e.programId === pending.programId && e.status === "ACTIVE",
      );

    return { pending: alreadyEntitled ? null : pending, isResolved: true };
  }, [data, isError, programId]);

  return (
    <PendingPaymentContext.Provider value={value}>{children}</PendingPaymentContext.Provider>
  );
};
